import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, getToken } from "@/lib/api";
import { Printer, ArrowLeft } from "lucide-react";
import { FloralBranch, OrnamentDivider } from "@/components/Floral";

const Card = ({ table, settings, token }) => (
  <div className="print-card flex items-center justify-center">
    <div
      className="relative text-center px-10 py-12 overflow-hidden"
      style={{ width: "128mm", minHeight: "170mm", backgroundColor: "var(--paper)", border: "1px solid var(--gold-soft)", outline: "4px solid var(--paper)", outlineOffset: "-9px", boxShadow: "inset 0 0 0 8px var(--paper), inset 0 0 0 9px var(--gold-soft)" }}
      data-testid={`print-card-${table.name}`}
    >
      <FloralBranch className="absolute -top-3 -left-6 w-36 opacity-70 pointer-events-none" />
      <FloralBranch flip className="absolute -bottom-3 -right-6 w-36 opacity-70 pointer-events-none rotate-180" />
      <p className="label-caps text-[0.6rem]" style={{ color: "var(--gold)" }}>
        {table.name}
      </p>
      <p className="font-script text-5xl mt-6 leading-tight" style={{ color: "var(--ink)" }}>
        {settings?.couple_name_1} &amp; {settings?.couple_name_2}
      </p>
      <OrnamentDivider className="my-6" />
      <p className="label-caps text-xs" style={{ color: "var(--ink)" }}>
        Fånga ögonblicket
      </p>
      <p className="font-serif-d italic text-lg mt-2" style={{ color: "var(--ink-soft)" }}>
        Hjälp oss minnas dagen genom era ögon.
      </p>
      <div className="inline-block mt-6 p-3 bg-white" style={{ border: "1px solid var(--gold-soft)" }}>
        <img src={`${API}/admin/tables/${table.id}/qr?auth=${token}`} alt={`QR ${table.name}`} className="w-44 h-44" />
      </div>
      <p className="font-serif-d text-base mt-6 leading-relaxed max-w-xs mx-auto" style={{ color: "var(--ink-soft)" }}>
        Skanna QR-koden med mobilkameran och dela era foton från kvällen
      </p>
      <div className="hairline w-16 mx-auto mt-6" />
      <p className="label-caps text-[0.55rem] mt-4" style={{ color: "var(--gold)" }}>
        {settings?.wedding_date}
      </p>
    </div>
  </div>
);

export default function PrintCards() {
  const { tableId } = useParams();
  const [tables, setTables] = useState(null);
  const [settings, setSettings] = useState(null);
  const token = getToken();

  useEffect(() => {
    Promise.all([api.get("/admin/tables"), api.get("/settings")])
      .then(([t, s]) => {
        setTables(tableId ? t.data.filter((x) => x.id === tableId) : t.data);
        setSettings(s.data);
      })
      .catch(() => setTables([]));
  }, [tableId]);

  if (!token)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-serif-d italic">Logga in via /admin först.</p>
      </div>
    );
  if (!tables || !settings) return <div className="min-h-screen" />;

  return (
    <div className="min-h-screen py-8">
      <div className="no-print flex items-center justify-center gap-3 mb-8">
        <a href="/admin" className="btn-invite text-[0.6rem] px-4 py-2 no-underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Tillbaka
        </a>
        <button className="btn-invite btn-solid text-[0.6rem] px-4 py-2" onClick={() => window.print()} data-testid="print-cards-btn">
          <Printer className="w-3.5 h-3.5" /> Skriv ut {tables.length > 1 ? `alla ${tables.length} kort` : "kort"}
        </button>
      </div>
      <div className="flex flex-col items-center gap-10">
        {tables.map((t) => (
          <Card key={t.id} table={t} settings={settings} token={token} />
        ))}
        {tables.length === 0 && <p className="font-serif-d italic">Inga bord hittades.</p>}
      </div>
    </div>
  );
}
